import React from 'react';
import styled from 'styled-components';
import { useTooltips } from '../context/TooltipContext';

const TooltipBubble = styled.div<{ $visible: boolean; $x: number; $y: number; $pos: string }>`
  position: fixed;
  top: 0; left: 0;
  transform: translate(calc(${p => p.$x}px - 50%), calc(${p => p.$y}px - 100% - 12px));
  background: rgba(25,25,30,0.92);
  color: #fff;
  font-size: 12px;
  line-height: 1.3;
  padding: 6px 9px 7px;
  border-radius: 8px;
  pointer-events: none;
  opacity: ${p => p.$visible ? 1 : 0};
  transform-origin: bottom center;
  transition: opacity .18s ease, transform .18s ease;
  box-shadow: 0 6px 18px -4px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.4);
  white-space: nowrap;
  z-index: 4000;
  ${p => p.$pos === 'left' ? 'transform: translate(calc('+p.$x+'px - 100% - 14px), calc('+p.$y+'px - 50%)); transform-origin: center right;' : ''}
  ${p => p.$pos === 'right' ? 'transform: translate(calc('+p.$x+'px + 14px), calc('+p.$y+'px - 50%)); transform-origin: center left;' : ''}
  ${p => p.$pos === 'bottom' ? 'transform: translate(calc('+p.$x+'px - 50%), calc('+p.$y+'px + 14px)); transform-origin: top center;' : ''}
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -6px;
    transform: translateX(-50%);
    width: 0; height: 0;
    border: 6px solid transparent;
    border-top-color: rgba(25,25,30,0.92);
    ${p => p.$pos === 'left' ? 'left:unset; right:-6px; top:50%; bottom:unset; transform:translateY(-50%); border:6px solid transparent; border-left-color: rgba(25,25,30,0.92);' : ''}
    ${p => p.$pos === 'right' ? 'left:-6px; top:50%; bottom:unset; transform:translateY(-50%); border:6px solid transparent; border-right-color: rgba(25,25,30,0.92);' : ''}
    ${p => p.$pos === 'bottom' ? 'top:-6px; bottom:unset; transform:translateX(-50%); border:6px solid transparent; border-bottom-color: rgba(25,25,30,0.92);' : ''}
  }
`;

export const TooltipLayer: React.FC = () => {
  const { enabled } = useTooltips();
  const [state, setState] = React.useState<{ text: string; x: number; y: number; pos: string; visible: boolean }>({ text:'', x:0, y:0, pos:'top', visible:false });
  const hideTimeout = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!enabled) {
      setState(s => ({ ...s, visible:false }));
      return;
    }
    const over = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target) return;
      const tip = target.getAttribute('data-tip');
      if (!tip) return;
      const rect = target.getBoundingClientRect();
      const pos = target.getAttribute('data-tip-pos') || 'top';
      if (hideTimeout.current) window.clearTimeout(hideTimeout.current);
      setState({ text: tip, x: rect.left + rect.width/2, y: rect.top, pos, visible:true });
    };
    const leave = (e: Event) => {
      if (hideTimeout.current) window.clearTimeout(hideTimeout.current);
      hideTimeout.current = window.setTimeout(() => setState(s => ({ ...s, visible:false })), 120);
    };
    document.addEventListener('mouseover', over);
    document.addEventListener('focusin', over);
    document.addEventListener('mouseout', leave);
    document.addEventListener('focusout', leave);
    return () => {
      document.removeEventListener('mouseover', over);
      document.removeEventListener('focusin', over);
      document.removeEventListener('mouseout', leave);
      document.removeEventListener('focusout', leave);
    };
  }, [enabled]);

  return state.text ? (
    <TooltipBubble role="tooltip" aria-hidden={!state.visible} $visible={state.visible} $x={state.x} $y={state.y} $pos={state.pos}>
      {state.text}
    </TooltipBubble>
  ) : null;
};

export default TooltipLayer;
